/**
 * Map GET Success Hub onto Progress. Express already hides tiles the signed-in
 * role cannot finish — this file does not invent a second capability matrix.
 *
 * Score is Kitchen on OMS points (no percent). Tile stats are live counts and
 * the next config threshold, never “2 / 5 recipes = 40%”.
 */

import { scrubInternalOrFixtureCopy } from './growthEngineInternalTitle'

export const GROWTH_ENGINE_SUCCESS_HUB_SURFACE = 'success_hub'

/** Ollie check that is still today’s kitchen job — never claim Done for today. */
export const SUCCESS_HUB_OVERDUE_FS_CHECK_ID = 'food_safety_overdue'

/**
 * Check id of today’s kitchen job when it is an Ollie pick (or a pick-shaped
 * hero). Ladder jobs without `olliePick` return null.
 */
export function successHubKitchenJobCheckId(source) {
  if (!source) return null
  const id =
    source.olliePick?.checkId ||
    source.checkId ||
    source.hero?.olliePick?.checkId ||
    source.today?.olliePick?.checkId ||
    null
  return id ? String(id) : null
}

export function isSuccessHubOverdueFsKitchenJob(source) {
  return successHubKitchenJobCheckId(source) === SUCCESS_HUB_OVERDUE_FS_CHECK_ID
}

/**
 * Fold cue says “Done for today”. Hide it while the board’s job is still an
 * overdue Food Safety check. Locked-in proof can still sit below.
 */
export function shouldShowSuccessHubFoldCue({
  hasProof = false,
  kitchenJob = null,
} = {}) {
  return Boolean(hasProof) && !isSuccessHubOverdueFsKitchenJob(kitchenJob)
}

/** When FS is overdue, momentum is health — never a 100% locked-in shout. */
export const SUCCESS_HUB_OVERDUE_FS_MOMENTUM_FALLBACK =
  'Food safety still needs a check today.'

export function formatSuccessHubOverdueFsMomentumLine(health) {
  return (
    formatSuccessHubHealthFootnote(health) ||
    SUCCESS_HUB_OVERDUE_FS_MOMENTUM_FALLBACK
  )
}

/**
 * Earliest UK calendar day a Progress receipt date can plausibly show.
 * Pre-OMS years (e.g. 12 Nov 2005) look broken even when Mongo has a timestamp.
 */
export const SUCCESS_HUB_DISPLAY_DATE_FLOOR = '2014-01-01T00:00:00.000Z'

/**
 * OR/OL (and peers) already live as receipt badges — hide the duplicate tile.
 * Keep in sync with Express `tileBoard.suppressWhenReceiptedFeatureIds`.
 */
export const SUCCESS_HUB_SUPPRESS_TILE_WHEN_RECEIPTED = Object.freeze([
  'ordering',
  'ollie_ordering',
])

/** Closed colour families from Success Hub config `badgeCategories`. */
export const SUCCESS_HUB_BADGE_CATEGORY_COLOURS = Object.freeze({
  ordering: 'seaweedGreen',
  kitchen: 'lettuceGreen',
  suppliers: 'bbqBlack',
  finance: 'oliveGrey',
  team: 'avocadoGreen',
  /** Black Belt — dark seaweed plate, not kitchen lime. */
  prestige: 'seaweedGreen',
})

/** Mark sizes on Progress — letter marks stay coded, not medals. */
export const SUCCESS_HUB_BADGE_MARK_SIZE = Object.freeze({
  tile: 32,
  receipt: 48,
  prestige: 56,
})

const LIGHT_BADGE_TOKENS = new Set(['lettuceGreen', 'avocadoGreen'])

/** Feature tile → existing Growth Engine CTA action (same routes as hero). */
export const SUCCESS_HUB_TILE_CTA_ACTIONS = Object.freeze({
  ordering: 'open_orders',
  ollie_ordering: 'open_ollie_ordering',
  suppliers: 'add_supplier',
  invoice_uploads: 'upload_invoice',
  par_levels: 'open_par_levels',
  stock_takes: 'start_stock_take',
  recipes: 'open_menu_builder',
  restricted_suppliers: 'restrict_supplier',
  standing_orders: 'open_standing_orders',
  goods_receipting: 'open_goods_receipting',
})

export function successHubTileCtaAction(featureId) {
  return SUCCESS_HUB_TILE_CTA_ACTIONS[featureId] || null
}

export function mapGrowthEngineSuccessHubPayload(response = {}) {
  const today = response.today || null
  const hero = response.hero || null
  const achievements = listVisibleSuccessHubAchievements(response.achievements)
  const receipts = mapSuccessHubReceipts(response.receipts)
  const tiles = listVisibleSuccessHubTiles(response.tiles, {
    achievements,
    receipts,
  })
  const score = mapSuccessHubScore(response.score)

  return {
    plan: response.plan ?? null,
    platform: response.platform || 'dashboard',
    today,
    todayReason: response.todayReason || null,
    hero,
    missions: listVisibleSuccessHubMissions(response.missions, today),
    score,
    health: response.health || null,
    tiles,
    achievements,
    collections: Array.isArray(response.collections)
      ? response.collections
      : [],
    receipts,
    celebration: mapSuccessHubCelebration(response.celebration),
    streaks: Array.isArray(response.streaks) ? response.streaks : [],
    businessDays: response.businessDays || null,
    blackBelt: mapSuccessHubBlackBelt(response.blackBelt),
    activationStages: mapSuccessHubActivationStages(response.activationStages),
    prefs: response.prefs || null,
    ollieRefreshing: Boolean(response.ollieRefreshing),
  }
}

/**
 * Named activation stages from Express — collections / Black Belt, not %.
 *
 * @param {object[]|null|undefined} stages
 * @returns {object[]}
 */
export function mapSuccessHubActivationStages(stages) {
  if (!Array.isArray(stages)) return []
  return stages
    .filter(stage => stage?.id && stage?.displayName)
    .map(stage => ({
      id: stage.id,
      displayName: stage.displayName,
      badge: stage.badge || null,
      criteria: stage.criteria || null,
      earned: Boolean(stage.earned),
      earnedAt: stage.earnedAt || null,
      current: Boolean(stage.current),
    }))
}

function mapSuccessHubScore(score) {
  if (!score || typeof score !== 'object') return null
  const points = Number(score.points)
  const maxPoints = Number(score.maxPoints)
  return {
    modelId: score.modelId || null,
    name: score.name || 'Kitchen on OMS',
    points: Number.isFinite(points) ? points : 0,
    maxPoints: Number.isFinite(maxPoints) ? maxPoints : 0,
  }
}

export function listVisibleSuccessHubTiles(
  tiles,
  { achievements, receipts } = {},
) {
  if (!Array.isArray(tiles)) return []
  const receiptedFeatures = new Set()
  for (const item of achievements || []) {
    if (item?.earned && item.featureId) receiptedFeatures.add(item.featureId)
  }
  for (const item of receipts?.recent || []) {
    if (item?.earned && item.featureId) receiptedFeatures.add(item.featureId)
  }
  const suppress = new Set(SUCCESS_HUB_SUPPRESS_TILE_WHEN_RECEIPTED)
  return tiles.filter(tile => {
    if (!tile?.featureId) return false
    if (suppress.has(tile.featureId) && receiptedFeatures.has(tile.featureId)) {
      return false
    }
    return true
  })
}

export function listVisibleSuccessHubAchievements(achievements) {
  if (!Array.isArray(achievements)) return []
  return achievements.filter(item => Boolean(item?.id))
}

export function listEarnedSuccessHubAchievements(achievements) {
  return listVisibleSuccessHubAchievements(achievements).filter(
    item => item.earned,
  )
}

export function mapSuccessHubReceipts(receipts) {
  const recent = listVisibleSuccessHubAchievements(receipts?.recent).filter(
    item => item.earned,
  )
  const featuredCollection = receipts?.featuredCollection?.id
    ? receipts.featuredCollection
    : null
  const collapsedCollections = Array.isArray(receipts?.collapsedCollections)
    ? receipts.collapsedCollections.filter(isSuccessHubCollectionVisible)
    : []
  return {
    recent,
    featuredCollection,
    collapsedCollections,
  }
}

/**
 * 29.46 — Locked-in receipt grid rhythm.
 * Prefer a filled 2×2 when there are exactly four cards. For five or more,
 * show three plus a quiet “+N more” cell so the fourth never sits alone on
 * row two under a three-column wrap.
 *
 * @param {number} totalCount
 * @returns {{ visible: number, more: number, columns: number }}
 */
export function planSuccessHubReceiptGrid(totalCount) {
  const total = Math.max(0, Math.floor(Number(totalCount) || 0))
  if (total <= 0) return { visible: 0, more: 0, columns: 1 }
  if (total <= 3) {
    return { visible: total, more: 0, columns: total }
  }
  if (total === 4) {
    return { visible: 4, more: 0, columns: 2 }
  }
  return { visible: 3, more: total - 3, columns: 2 }
}

/**
 * Quiet overflow label for the receipt grid “+N more” cell (29.46).
 *
 * @param {number} moreCount
 * @returns {string}
 */
export function formatSuccessHubReceiptsMoreLabel(moreCount) {
  const n = Math.max(0, Math.floor(Number(moreCount) || 0))
  if (n <= 0) return ''
  return n === 1 ? '+1 more' : `+${n} more`
}

/**
 * Complete or started collections only — hide empty 0/n husks.
 *
 * @param {object|null|undefined} collection
 * @returns {boolean}
 */
export function isSuccessHubCollectionVisible(collection) {
  if (!collection?.id) return false
  if (collection.complete) return true
  return Number(collection.earnedCount) > 0
}

/**
 * Whether a collection set is finished (all badges locked in).
 *
 * @param {object|null|undefined} collection
 * @returns {boolean}
 */
export function isSuccessHubCollectionComplete(collection) {
  if (!collection) return false
  if (collection.complete) return true
  const earned = Number(collection.earnedCount)
  const total = Number(collection.total)
  return (
    Number.isFinite(earned) &&
    Number.isFinite(total) &&
    total > 0 &&
    earned >= total
  )
}

/**
 * Quiet summary when finished collections collapse under Locked in (29.43).
 *
 * @param {number} count
 * @returns {string|null}
 */
export function formatSuccessHubCollapsedSetsSummary(count) {
  const n = Math.floor(Number(count))
  if (!Number.isFinite(n) || n <= 0) return null
  return n === 1 ? '1 set locked in' : `${n} sets locked in`
}

export function mapSuccessHubCelebration(celebration) {
  const outcome = String(celebration?.outcome || '').trim()
  if (!outcome) return null
  return {
    achievementId: celebration.achievementId || null,
    featureId: celebration.featureId || null,
    displayName: celebration.displayName || null,
    outcome,
    badge: celebration.badge || null,
    earnedAt: celebration.earnedAt || null,
  }
}

export function shouldShowSuccessHubCelebration(hub) {
  if (hub?.prefs?.celebrations?.enabled === false) return false
  return Boolean(mapSuccessHubCelebration(hub?.celebration)?.outcome)
}

export function mapSuccessHubBlackBelt(blackBelt) {
  if (!blackBelt?.id && !blackBelt?.earned && !blackBelt?.displayName) {
    return null
  }
  const congratulations = String(blackBelt?.congratulations || '').trim()
  const celebrate = Boolean(blackBelt?.celebrate) && Boolean(congratulations)
  const whyItMatters = resolveSuccessHubReceiptWhy(blackBelt)
  return {
    id: blackBelt.id || 'oms_black_belt',
    displayName: blackBelt.displayName || 'OMS Black Belt',
    badge: blackBelt.badge || { initials: 'BB', categoryId: 'prestige' },
    earned: Boolean(blackBelt.earned),
    earnedAt: blackBelt.earnedAt || null,
    quiet: Boolean(blackBelt.quiet),
    missing: blackBelt.missing || null,
    whyItMatters,
    congratulations: celebrate ? congratulations : null,
    celebrate,
  }
}

/**
 * Receipt cards always show a why line when Express sent one (or a
 * displayName fallback). Never invent rarity copy.
 *
 * @param {{ whyItMatters?: string|null, outcome?: string|null, title?: string|null, displayName?: string|null }} [item]
 * @returns {string|null}
 */
export function resolveSuccessHubReceiptWhy(item) {
  const why = String(item?.whyItMatters || '').trim()
  if (why) return why
  const outcome = String(item?.outcome || '').trim()
  if (outcome) return outcome
  return null
}

export function shouldShowSuccessHubBlackBelt(hub) {
  return Boolean(mapSuccessHubBlackBelt(hub?.blackBelt)?.earned)
}

export function shouldCelebrateSuccessHubBlackBelt(hub) {
  if (hub?.prefs?.celebrations?.enabled === false) return false
  const belt = mapSuccessHubBlackBelt(hub?.blackBelt)
  return Boolean(belt?.earned && belt.celebrate && belt.congratulations)
}

export function formatSuccessHubCollectionSummary(collection) {
  const name = collection?.displayName || ''
  if (!name) return ''
  const earned = Number(collection.earnedCount)
  const total = Number(collection.total)
  if (!Number.isFinite(earned) || !Number.isFinite(total) || total <= 0) {
    return name
  }
  if (collection.complete || earned >= total) {
    return `${name} · ${total} of ${total} · set locked in`
  }
  const left = Math.max(0, total - earned)
  if (earned > 0) {
    return `${name} · ${earned} of ${total} · ${left} left`
  }
  return `${name} · 0 of ${total} · ${total} left`
}

/**
 * Obvious next unlock on a collection row — not buried in an accordion.
 *
 * @param {object|null|undefined} collection
 * @returns {string|null}
 */
export function formatSuccessHubCollectionNext(collection) {
  if (!collection || collection.complete) return null
  const next = String(collection.next?.displayName || '').trim()
  if (!next) return null
  return `Next · ${next}`
}

/**
 * Chef-obvious “what to do” under the next unlock — action, not jargon.
 *
 * @param {object|null|undefined} collection
 * @returns {string|null}
 */
export function formatSuccessHubCollectionNextHow(collection) {
  if (!collection || collection.complete) return null
  const how = String(collection.next?.howToComplete || '').trim()
  if (how) return how
  // Fallback when older payloads lack howToComplete — still show the benefit.
  const why = String(collection.next?.whyItMatters || '').trim()
  return why || null
}

/**
 * Whole-kitchen achievement set — “4 of 12 locked in · 8 left”.
 * Counts every achievement Express sent for this role/venue (not tiles).
 *
 * @param {object[]|null|undefined} achievements
 * @returns {{ earned: number, total: number, left: number, label: string }|null}
 */
export function formatSuccessHubAchievementSetProgress(achievements) {
  const list = listVisibleSuccessHubAchievements(achievements)
  const total = list.length
  if (!total) return null
  const earned = list.filter(item => item.earned).length
  const left = Math.max(0, total - earned)
  if (left === 0) {
    return {
      earned,
      total,
      left: 0,
      label: `${earned} of ${total} locked in · set complete`,
    }
  }
  return {
    earned,
    total,
    left,
    label: `${earned} of ${total} locked in · ${left} left`,
  }
}

export function listVisibleSuccessHubMissions(missions, today) {
  if (!Array.isArray(missions)) return []
  const todayId = today?.intentId
  return missions
    .filter(item => item?.intentId && item.intentId !== todayId)
    .map(mapSuccessHubMission)
}

/**
 * Progress leftover mission — preserve Express mark / benefit / minutes.
 *
 * @param {object} mission
 * @returns {object}
 */
export function mapSuccessHubMission(mission = {}) {
  return {
    intentId: mission.intentId || null,
    featureId: mission.featureId || null,
    title: scrubInternalOrFixtureCopy(mission.title || ''),
    body: scrubInternalOrFixtureCopy(mission.body || ''),
    cta: mission.cta || null,
    progress: mission.progress || null,
    variantIndex: mission.variantIndex,
    recommendationType: mission.recommendationType || null,
    estimatedMinutes:
      mission.estimatedMinutes != null
        ? Number(mission.estimatedMinutes)
        : null,
    badge: mission.badge || null,
    whyItMatters:
      scrubInternalOrFixtureCopy(mission.whyItMatters || '') || null,
    continueOn: mission.continueOn || null,
    honestNote: mission.honestNote || null,
  }
}

export function mapSuccessHubHeroToRecommendation(hero, today) {
  const source = hero || today
  if (!source?.title && !source?.body) return null
  return {
    intentId: source.intentId || today?.intentId || null,
    featureId: source.featureId || today?.featureId || null,
    title: scrubInternalOrFixtureCopy(source.title || ''),
    body: scrubInternalOrFixtureCopy(source.body || ''),
    cta: source.cta || today?.cta || null,
    variantIndex: source.variantIndex,
    continueOn: source.continueOn || today?.continueOn,
    honestNote: source.honestNote || today?.honestNote,
    estimatedMinutes:
      source.estimatedMinutes ?? today?.estimatedMinutes ?? null,
    askOllie: source.askOllie || today?.askOllie || null,
    olliePick: source.olliePick || today?.olliePick || null,
  }
}

export function formatSuccessHubScoreFootnote(score) {
  if (!score) return null
  const name = score.name || 'Kitchen on OMS'
  const points = Number(score.points)
  if (!Number.isFinite(points)) return name
  const unit = points === 1 ? 'point' : 'points'
  return `${name} · ${points} ${unit}`
}

/**
 * Quiet health line — the working/gap story, not a recency dump or %.
 *
 * @param {object|null|undefined} health
 * @returns {string|null}
 */
export function formatSuccessHubHealthFootnote(health) {
  const story = String(health?.story || '').trim()
  if (story) return story
  const summary = String(health?.summary || '').trim()
  return summary || null
}

/**
 * Honest Kitchen on OMS ratio — points / maxPoints only (0–1).
 * Display chrome maps this through {@link successHubActivationDisplayPercent}
 * so the Progress bar starts at a rewarding 30% floor.
 *
 * @param {object|null|undefined} score
 * @returns {number|null} 0–1 inclusive, or null when score is unusable
 */
export function successHubActivationRatio(score) {
  const points = Number(score?.points)
  const maxPoints = Number(score?.maxPoints)
  if (
    !Number.isFinite(points) ||
    !Number.isFinite(maxPoints) ||
    maxPoints <= 0
  ) {
    return null
  }
  return Math.min(1, Math.max(0, points / maxPoints))
}

/** Progress bar floor — opening Progress already earns a start, not 0%. */
export const SUCCESS_HUB_ACTIVATION_DISPLAY_FLOOR = 0.3

/**
 * Map honest ratio → Progress bar fill. 0 points → 30%, full → 100%.
 *
 * @param {number|null|undefined} ratio 0–1 honest activation
 * @returns {number|null} 0–1 display fill
 */
export function successHubActivationDisplayRatio(ratio) {
  if (ratio == null || !Number.isFinite(Number(ratio))) return null
  const honest = Math.min(1, Math.max(0, Number(ratio)))
  return (
    SUCCESS_HUB_ACTIVATION_DISPLAY_FLOOR +
    honest * (1 - SUCCESS_HUB_ACTIVATION_DISPLAY_FLOOR)
  )
}

/**
 * @param {number|null|undefined} ratio 0–1 honest activation
 * @returns {number|null} whole percent 30–100
 */
export function successHubActivationDisplayPercent(ratio) {
  const display = successHubActivationDisplayRatio(ratio)
  if (display == null) return null
  return Math.round(display * 100)
}

/**
 * Progress label — percent complete toward Kitchen on OMS (30% floor).
 *
 * @param {object|null|undefined} score
 * @returns {string|null}
 */
export function formatSuccessHubActivationLabel(score) {
  if (!score) return null
  const ratio = successHubActivationRatio(score)
  if (ratio == null) return score.name || 'Kitchen on OMS'
  const percent = successHubActivationDisplayPercent(ratio)
  return `${percent}% complete · Kitchen on OMS`
}

/**
 * Short professional encouragement for the Progress momentum strip.
 * The big percent lives in the activation hero — this line is the nudge, not a
 * second readout. Mature boards get a quiet win line.
 *
 * @param {{
 *   health?: object|null,
 *   activationRatio?: number|null,
 *   mature?: boolean,
 * }} input
 * @returns {string|null}
 */
export function formatSuccessHubMomentumEncouragement({
  health,
  activationRatio,
  mature = false,
  overdueFs = false,
} = {}) {
  if (overdueFs) {
    return formatSuccessHubOverdueFsMomentumLine(health)
  }
  if (mature) {
    return 'Today’s job keeps it sharp.'
  }
  const percent = successHubActivationDisplayPercent(activationRatio)
  if (percent != null && percent < 100) {
    if (percent <= 35) {
      return 'Useful jobs on OMS lift this — and get more from the account.'
    }
    if (percent < 70) {
      return 'Keep finishing useful jobs to lift this percentage — and get more from OMS.'
    }
    return 'Almost there — useful jobs lift you further so you get the most from OMS.'
  }
  const story = String(health?.story || '').trim()
  if (story) return story
  const summary = String(health?.summary || '').trim()
  if (summary) return summary
  if (activationRatio == null) return null
  return 'Useful jobs on OMS lift this — and get more from the account.'
}

/**
 * Full Kitchen on OMS or earned Black Belt — quieter chrome, fewer leftover nags.
 *
 * @param {{ score?: object|null, blackBelt?: object|null }} input
 * @returns {boolean}
 */
export function isSuccessHubMatureBoard({ score, blackBelt } = {}) {
  if (blackBelt?.earned) return true
  const points = Number(score?.points)
  const maxPoints = Number(score?.maxPoints)
  return (
    Number.isFinite(points) &&
    Number.isFinite(maxPoints) &&
    maxPoints > 0 &&
    points >= maxPoints
  )
}

/**
 * Achievement set nearly done — ≤1 left or ≥85% locked in.
 * Used with mature Kitchen / Black Belt so tiles don’t restart the board.
 *
 * @param {{ earned?: number, total?: number, left?: number }|null|undefined} setProgress
 * @returns {boolean}
 */
export function isSuccessHubNearlyCompleteSet(setProgress) {
  const total = Number(setProgress?.total)
  const earned = Number(setProgress?.earned)
  const left =
    setProgress?.left != null
      ? Number(setProgress.left)
      : Number.isFinite(total) && Number.isFinite(earned)
      ? Math.max(0, total - earned)
      : NaN
  if (!Number.isFinite(total) || total <= 0) return false
  if (Number.isFinite(left) && left === 0) return true
  // Small early sets (e.g. 1 of 2) are not “nearly done” — need a real kitchen set.
  if (Number.isFinite(left) && left <= 1 && total >= 4) return true
  if (!Number.isFinite(earned)) return false
  return earned / total >= 0.85
}

/**
 * Mature Kitchen / Black Belt, or a nearly-complete achievement set —
 * tiles read as status, not a second “Still open” restart wall.
 *
 * @param {{
 *   score?: object|null,
 *   blackBelt?: object|null,
 *   setProgress?: { earned?: number, total?: number, left?: number }|null,
 * }} input
 * @returns {boolean}
 */
export function isSuccessHubMatureTilesSection({
  score,
  blackBelt,
  setProgress,
} = {}) {
  return (
    isSuccessHubMatureBoard({ score, blackBelt }) ||
    isSuccessHubNearlyCompleteSet(setProgress)
  )
}

/**
 * Locked-in tiles cue (29.44). Early boards nudge leftover wins; mature /
 * nearly-complete boards use status copy — or hide when the set is done.
 *
 * @param {{
 *   matureTiles?: boolean,
 *   setProgress?: { left?: number }|null,
 * }} input
 * @returns {string|null}
 */
export function formatSuccessHubTilesCue({
  matureTiles = false,
  setProgress = null,
} = {}) {
  if (!matureTiles) {
    return 'Still open — each mark below is another win you can lock in.'
  }
  if (Number(setProgress?.left) === 0) return null
  return 'Live counts on this kitchen.'
}

/**
 * Plain-language next easy job from today’s hero (same picker as Progress).
 *
 * @param {{ hero?: object|null, today?: object|null }} input
 * @returns {{ label: string, featureId?: string, intentId?: string, cta?: object }|null}
 */
export function formatSuccessHubNextEasyJob({ hero, today } = {}) {
  const source = hero || today
  if (!source || typeof source !== 'object') return null
  const ctaLabel = String(source.cta?.label || '').trim()
  const title = String(source.title || '').trim()
  const featureId = source.featureId || null
  const intentId = String(source.intentId || '')
  let label = null
  if (featureId === 'recipes') {
    if (/cost another/i.test(ctaLabel)) label = 'You can cost another dish'
    else if (
      ctaLabel ||
      /first|habit|making money|GP/i.test(intentId + title)
    ) {
      label = 'You can cost 1 dish'
    }
  } else if (
    featureId === 'ollie_ordering' &&
    (/first/i.test(intentId) || ctaLabel)
  ) {
    label = 'You can build an order in one ask'
  } else if (
    featureId === 'stock_takes' &&
    (/first/i.test(intentId) || ctaLabel)
  ) {
    label = 'You can count what is on the shelf'
  } else if (
    featureId === 'par_levels' &&
    (/first/i.test(intentId) || ctaLabel)
  ) {
    label = 'You can set 1 par'
  } else if (
    featureId === 'ordering' &&
    (/first/i.test(intentId) || ctaLabel)
  ) {
    label = 'You can place the next order'
  }
  if (!label && ctaLabel) {
    label = ctaLabel.startsWith('You ') ? ctaLabel : `Next · ${ctaLabel}`
  }
  if (!label && title) label = title
  if (!label) return null
  return {
    label,
    featureId: featureId || undefined,
    intentId: source.intentId || undefined,
    cta: source.cta || null,
  }
}

/**
 * Momentum strip model: encouragement + honest Kitchen on OMS bar + named
 * stages + enabled streaks + next easy job. Returns null when nothing to show.
 *
 * @param {{
 *   score?: object|null,
 *   health?: object|null,
 *   hero?: object|null,
 *   today?: object|null,
 *   activationStages?: object[]|null,
 *   streaks?: object[]|null,
 *   blackBelt?: object|null,
 * }} input
 */
export function buildSuccessHubMomentumStrip({
  score,
  health,
  hero,
  today,
  activationStages,
  streaks,
  blackBelt,
} = {}) {
  const ratio = successHubActivationRatio(score)
  const nextJob = formatSuccessHubNextEasyJob({ hero, today })
  const stages = mapSuccessHubActivationStages(activationStages)
  const streakLines = listVisibleSuccessHubMomentumStreaks(streaks)
  const overdueFs = isSuccessHubOverdueFsKitchenJob(hero || today)
  const mature = overdueFs
    ? false
    : isSuccessHubMatureBoard({ score, blackBelt })
  const activation =
    overdueFs || ratio == null
      ? null
      : {
          name: score.name || 'Kitchen on OMS',
          points: Number(score.points) || 0,
          maxPoints: Number(score.maxPoints),
          ratio,
          displayRatio: successHubActivationDisplayRatio(ratio),
          displayPercent: successHubActivationDisplayPercent(ratio),
          label: formatSuccessHubActivationLabel(score),
        }
  const encouragement = formatSuccessHubMomentumEncouragement({
    health,
    activationRatio: ratio,
    mature,
    overdueFs,
  })
  if (
    !encouragement &&
    !activation &&
    !nextJob &&
    !stages.length &&
    !streakLines.length
  ) {
    return null
  }
  return {
    encouragement,
    activation,
    nextJob,
    stages,
    streaks: streakLines,
    mature,
    overdueFs,
  }
}

/** Ordering first, then weekly stock — Express already role-filters. */
const SUCCESS_HUB_MOMENTUM_STREAK_ORDER = Object.freeze([
  'ordering_on_your_days',
  'weekly_stock_take',
])

/** Short chip labels — habit language, not product jargon. */
const SUCCESS_HUB_STREAK_CHIP_LABELS = Object.freeze({
  ordering_on_your_days: 'Ordering habit',
  weekly_stock_take: 'Shelf habit',
})

/** First-time hover copy — what the chip / summary number actually means. */
const SUCCESS_HUB_STREAK_TOOLTIPS = Object.freeze({
  ordering_on_your_days:
    'How many of your set ordering days you’ve ordered in a row. Days you don’t order on don’t break it. “Best” is your longest run.',
  weekly_stock_take:
    'How many weeks in a row you’ve finished a stock take. “Best” is your longest run.',
})

/** Named stage hover copy — outcome language, not jargon. */
const SUCCESS_HUB_STAGE_TOOLTIPS = Object.freeze({
  kitchen_lit: 'You’ve sent an order — this kitchen is live on OMS.',
  ordering_habit: 'Ordering has stuck as a habit here, not a one-off.',
  shelf_honest: 'Stock counts and pars keep the shelf honest.',
  menu_costed: 'Dishes have costs so you can see whether a plate makes money.',
  oms_black_belt:
    'Core kitchen is locked in: ordering habit, suppliers, invoices, par, stock takes, and recipes.',
})

/**
 * Enabled streaks only (days confirmed). Used for strip model data —
 * Progress UI shows counts in the top summary instead.
 *
 * @param {object[]|null|undefined} streaks
 * @returns {object[]}
 */
export function listVisibleSuccessHubMomentumStreaks(streaks) {
  if (!Array.isArray(streaks)) return []
  const enabled = streaks.filter(
    row => row?.enabled && row?.streakId && row?.displayName,
  )
  return [...enabled]
    .sort((left, right) => {
      const leftRank = SUCCESS_HUB_MOMENTUM_STREAK_ORDER.indexOf(left.streakId)
      const rightRank = SUCCESS_HUB_MOMENTUM_STREAK_ORDER.indexOf(
        right.streakId,
      )
      const a =
        leftRank === -1 ? SUCCESS_HUB_MOMENTUM_STREAK_ORDER.length : leftRank
      const b =
        rightRank === -1 ? SUCCESS_HUB_MOMENTUM_STREAK_ORDER.length : rightRank
      if (a !== b) return a - b
      return String(left.streakId).localeCompare(String(right.streakId))
    })
    .map(row => {
      const current = Math.max(0, Number(row.current) || 0)
      const longest = Math.max(0, Number(row.longest) || 0)
      const label =
        SUCCESS_HUB_STREAK_CHIP_LABELS[row.streakId] || row.displayName
      return {
        streakId: row.streakId,
        featureId: row.featureId || null,
        displayName: row.displayName,
        label,
        cadence: row.cadence || null,
        current,
        longest,
        lastCountedOn: row.lastCountedOn || null,
        copy: formatSuccessHubStreakLine(row),
        tooltip: formatSuccessHubStreakTooltip(row),
      }
    })
    .filter(row => Boolean(row.copy))
}

/** Top-summary labels — “streak” so zero still reads as a count. */
const SUCCESS_HUB_SUMMARY_STREAK_LABELS = Object.freeze({
  ordering_on_your_days: 'Ordering streak',
  weekly_stock_take: 'Shelf streak',
})

const SUCCESS_HUB_ORDERING_STREAK_FALLBACK = Object.freeze({
  streakId: 'ordering_on_your_days',
  featureId: 'ordering',
  displayName: 'Ordered on your days',
  cadence: 'configured_ordering_days',
  enabled: false,
  current: 0,
  longest: 0,
  lastCountedOn: null,
})

/**
 * Top-of-Progress streak rows — ordering always, even at 0 / days unconfirmed.
 *
 * @param {object[]|null|undefined} streaks
 * @returns {object[]}
 */
export function listSuccessHubProgressSummaryStreaks(streaks) {
  const byId = new Map(
    (Array.isArray(streaks) ? streaks : [])
      .filter(row => row?.streakId)
      .map(row => [row.streakId, row]),
  )
  const ordering =
    byId.get('ordering_on_your_days') || SUCCESS_HUB_ORDERING_STREAK_FALLBACK
  const current = Math.max(0, Number(ordering.current) || 0)
  const longest = Math.max(0, Number(ordering.longest) || 0)
  const label =
    SUCCESS_HUB_SUMMARY_STREAK_LABELS[ordering.streakId] ||
    SUCCESS_HUB_STREAK_CHIP_LABELS[ordering.streakId] ||
    ordering.displayName ||
    'Ordering streak'
  return [
    {
      streakId: ordering.streakId,
      featureId: ordering.featureId || 'ordering',
      displayName: ordering.displayName || label,
      label,
      enabled: Boolean(ordering.enabled),
      current,
      longest,
      lastCountedOn: ordering.lastCountedOn || null,
      tooltip: formatSuccessHubStreakTooltip(ordering),
    },
  ]
}

/**
 * Habit chip — always shows the current run count so “best 3” alone never
 * looks like today’s streak. Enabled streaks only (days confirmed).
 *
 * @param {object|null|undefined} streak
 * @returns {string|null}
 */
export function formatSuccessHubStreakLine(streak) {
  if (!streak?.enabled) return null
  const fromId = SUCCESS_HUB_STREAK_CHIP_LABELS[streak.streakId]
  const name = String(fromId || streak.displayName || '').trim()
  if (!name) return null
  const current = Math.max(0, Number(streak.current) || 0)
  const longest = Math.max(0, Number(streak.longest) || 0)
  const run = `${name} · ${current} in a row`
  if (longest > current) return `${run} · best ${longest}`
  return run
}

/**
 * Hover explanation for a streak chip or summary tile.
 *
 * @param {object|null|undefined} streak
 * @returns {string|null}
 */
export function formatSuccessHubStreakTooltip(streak) {
  if (!streak) return null
  const fromId = SUCCESS_HUB_STREAK_TOOLTIPS[streak.streakId]
  if (fromId) return fromId
  const name = String(
    SUCCESS_HUB_STREAK_CHIP_LABELS[streak.streakId] ||
      streak.displayName ||
      streak.label ||
      '',
  ).trim()
  if (!name) return null
  return `${name}: how many times in a row this habit has landed on schedule. “Best” is the longest run.`
}

/**
 * Hover explanation for a Kitchen on OMS activation stage mark.
 *
 * @param {object|null|undefined} stage
 * @returns {string|null}
 */
export function formatSuccessHubStageTooltip(stage) {
  if (!stage?.id && !stage?.displayName) return null
  const fromId = SUCCESS_HUB_STAGE_TOOLTIPS[stage?.id]
  if (fromId) {
    if (stage?.earned) return `${fromId} Done.`
    if (stage?.current) return `${fromId} This is the next mark on the ladder.`
    return fromId
  }
  const name = String(stage?.displayName || '').trim()
  return name ? `${name} — a named mark on the Kitchen on OMS ladder.` : null
}

/**
 * Hover for the Kitchen on OMS percent hero.
 *
 * @param {object|null|undefined} activation
 * @returns {string}
 */
export function formatSuccessHubActivationTooltip(activation) {
  const percent = activation?.displayPercent
  const base =
    'Percent complete toward running ordering, stock, and costing on OMS. Useful jobs here lift this. New kitchens start the bar at 30% so progress is never an empty line.'
  if (percent == null) return base
  return `${percent}% complete. ${base}`
}

/**
 * Top-of-Progress habit summary — ordering streak always visible (even at 0).
 * One short hint only — no doubled confirm/panic copy.
 * Mature / locked-in boards skip “Not running” panic when Best run already stands.
 *
 * @param {{
 *   streaks?: object[]|null,
 *   businessDays?: object|null,
 *   mature?: boolean,
 * }} input
 * @returns {{ daysConfirmed: boolean, hint: string, streaks: object[] }}
 */
export function buildSuccessHubProgressSummary({
  streaks,
  businessDays,
  mature = false,
} = {}) {
  const daysConfirmed = Boolean(businessDays?.confirmed)
  const optedOut = Boolean(businessDays?.optedOut) && !daysConfirmed
  const usingDefaults =
    Boolean(businessDays?.usingDefaultWeekdays) || (!daysConfirmed && !optedOut)
  const rows = listSuccessHubProgressSummaryStreaks(streaks)
  const primary = rows[0]
  const broken = primary && primary.current === 0 && primary.longest > 0

  let hint
  if (optedOut) {
    hint = 'Streaks paused — Change days to count again.'
  } else if (broken) {
    hint = mature
      ? 'Longest run still stands — order on your next day to pick it up.'
      : 'Not running — pick it up on your next ordering day.'
  } else if (daysConfirmed) {
    hint = mature
      ? 'Change days if the rhythm shifts.'
      : 'Best run is your longest — Change days if the rhythm shifts.'
  } else if (usingDefaults) {
    hint = 'Counts Mon–Fri by default — Change days if needed.'
  } else {
    hint = 'Change days if this kitchen’s rhythm shifts.'
  }

  return {
    daysConfirmed,
    hint,
    streaks: rows.map(row => ({
      streakId: row.streakId,
      label: row.label,
      current: row.current,
      longest: row.longest,
      enabled: row.enabled,
      currentLabel: String(row.current),
      unitLabel: 'ordering days in a row',
      bestLabel:
        row.longest > row.current || (row.current === 0 && row.longest > 0)
          ? `Best run ${row.longest}`
          : null,
      pendingLabel: null,
      tooltip: row.tooltip,
    })),
  }
}

/**
 * Current named stage for Progress chrome (never a vanity %).
 *
 * @param {object[]|null|undefined} stages
 * @returns {object|null}
 */
export function pickSuccessHubCurrentActivationStage(stages) {
  const list = mapSuccessHubActivationStages(stages)
  if (!list.length) return null
  return list.find(stage => stage.current) || list[list.length - 1] || null
}

export function formatSuccessHubTileStat(tile) {
  const count = Number(tile?.count)
  const safeCount = Number.isFinite(count) ? count : 0
  const next = tile?.nextThreshold
  const kind = tile?.nextThresholdKind
  const recency = tile?.recency?.copy
  const unit = String(tile?.countUnit || '').trim()
  const countLabel = unit ? `${safeCount} ${unit}` : String(safeCount)
  let countCopy = countLabel
  if (next != null && Number.isFinite(Number(next))) {
    const nextLabel =
      kind === 'achievement' ? `next badge at ${next}` : `next at ${next}`
    countCopy = `${countLabel} · ${nextLabel}`
  }
  return recency ? `${recency} · ${countCopy}` : countCopy
}

export function successHubBadgeColourToken(badge) {
  const categoryId = badge?.categoryId
  return SUCCESS_HUB_BADGE_CATEGORY_COLOURS[categoryId] || 'oliveGrey'
}

export function successHubBadgeUsesDarkText(colourToken) {
  return LIGHT_BADGE_TOKENS.has(colourToken)
}

/**
 * @param {{ variant?: 'mark'|'receipt', prestige?: boolean, size?: number }} [options]
 * @returns {number}
 */
export function successHubBadgeMarkSize({
  variant = 'mark',
  prestige = false,
  size,
} = {}) {
  if (Number.isFinite(size) && size > 0) {
    if (variant === 'receipt') {
      return Math.max(
        size,
        prestige
          ? SUCCESS_HUB_BADGE_MARK_SIZE.prestige
          : SUCCESS_HUB_BADGE_MARK_SIZE.receipt,
      )
    }
    return size
  }
  if (prestige) return SUCCESS_HUB_BADGE_MARK_SIZE.prestige
  if (variant === 'receipt') return SUCCESS_HUB_BADGE_MARK_SIZE.receipt
  return SUCCESS_HUB_BADGE_MARK_SIZE.tile
}

export function successHubBadgeInitials(badge, fallback = '') {
  const initials = String(badge?.initials || '').trim()
  if (initials) return initials.slice(0, 3)
  const fromName = String(fallback || '')
    .replace(/[^A-Za-z0-9£]/g, '')
    .slice(0, 2)
    .toUpperCase()
  return fromName || '·'
}

export const SUCCESS_HUB_UK_TIMEZONE = 'Europe/London'

/** ISO 1 = Monday … 7 = Sunday. Same as Express business days. */
export const SUCCESS_HUB_ISO_WEEKDAYS = Object.freeze([1, 2, 3, 4, 5, 6, 7])

/** ISO Mon–Fri — matches Express default until the kitchen confirms days. */
export const SUCCESS_HUB_DEFAULT_ORDERING_WEEKDAYS = Object.freeze([
  1, 2, 3, 4, 5,
])

export const SUCCESS_HUB_WEEKDAY_NAMES = Object.freeze({
  1: 'Monday',
  2: 'Tuesday',
  3: 'Wednesday',
  4: 'Thursday',
  5: 'Friday',
  6: 'Saturday',
  7: 'Sunday',
})

export const SUCCESS_HUB_WEEKDAY_SHORT = Object.freeze({
  1: 'Mon',
  2: 'Tue',
  3: 'Wed',
  4: 'Thu',
  5: 'Fri',
  6: 'Sat',
  7: 'Sun',
})

export function normalizeSuccessHubWeekdays(value) {
  if (!Array.isArray(value)) return []
  const seen = new Set()
  for (const entry of value) {
    const day = Number(entry)
    if (!Number.isInteger(day) || day < 1 || day > 7) continue
    seen.add(day)
  }
  return SUCCESS_HUB_ISO_WEEKDAYS.filter(day => seen.has(day))
}

export function successHubWeekdaysEqual(left, right) {
  const a = normalizeSuccessHubWeekdays(left)
  const b = normalizeSuccessHubWeekdays(right)
  if (a.length !== b.length) return false
  return a.every((day, index) => day === b[index])
}

/**
 * “Monday, Tuesday, and Wednesday” — suggestion copy, not a streak score.
 *
 * @param {number[]} weekdays
 * @returns {string}
 */
export function formatSuccessHubWeekdayList(weekdays) {
  const names = normalizeSuccessHubWeekdays(weekdays).map(
    day => SUCCESS_HUB_WEEKDAY_NAMES[day],
  )
  if (!names.length) return ''
  if (names.length === 1) return names[0]
  if (names.length === 2) return `${names[0]} and ${names[1]}`
  return `${names.slice(0, -1).join(', ')}, and ${names[names.length - 1]}`
}

export function formatSuccessHubOrderingSuggestion(businessDays) {
  const days = formatSuccessHubWeekdayList(businessDays?.suggestedWeekdays)
  if (!days) return ''
  return `Your recent orders usually go out on ${days}.`
}

/**
 * True when a receipt date is safe to show on Progress (not epoch junk / future).
 *
 * @param {string|Date|null|undefined} earnedAt
 * @param {Date} [now]
 * @returns {boolean}
 */
export function isPlausibleSuccessHubDisplayDate(earnedAt, now = new Date()) {
  if (earnedAt == null || earnedAt === '') return false
  const date = earnedAt instanceof Date ? earnedAt : new Date(earnedAt)
  if (Number.isNaN(date.getTime())) return false
  const floor = new Date(SUCCESS_HUB_DISPLAY_DATE_FLOOR)
  if (date.getTime() < floor.getTime()) return false
  const tomorrow = new Date(now)
  tomorrow.setUTCDate(tomorrow.getUTCDate() + 2)
  if (date.getTime() > tomorrow.getTime()) return false
  return true
}

/**
 * Earned date on a badge receipt — UK calendar day, not a rarity label.
 * Nonsense dates (e.g. 12 Nov 2005) are omitted rather than shown broken.
 *
 * @param {string|Date|null|undefined} earnedAt
 * @param {Date} [now]
 * @returns {string|null}
 */
export function formatSuccessHubBadgeDate(earnedAt, now = new Date()) {
  if (!isPlausibleSuccessHubDisplayDate(earnedAt, now)) return null
  const date = earnedAt instanceof Date ? earnedAt : new Date(earnedAt)
  // en-GB 'short' is "Sept" in newer browsers; the app and Ollie say "Sep".
  const [year, month, day] = new Intl.DateTimeFormat('en-CA', {
    timeZone: SUCCESS_HUB_UK_TIMEZONE,
  })
    .format(date)
    .split('-')
    .map(Number)
  return `${day} ${SUCCESS_HUB_SHORT_MONTHS[month - 1]} ${year}`
}

const SUCCESS_HUB_SHORT_MONTHS = Object.freeze([
  'Jan',
  'Feb',
  'Mar',
  'Apr',
  'May',
  'Jun',
  'Jul',
  'Aug',
  'Sep',
  'Oct',
  'Nov',
  'Dec',
])
